import { baseApi } from './baseApi';

/**
 * Proposals API endpoints
 * Handles CRUD operations and various proposal-related actions
 */
export const proposalsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /proposals - Fetch proposals with pagination, search, filtering, and sorting
    getProposals: builder.query({
      query: ({ 
        page = 1, 
        limit = 10, 
        search, 
        status_id, 
        prospect_id, 
        created_from, 
        created_to, 
        valid_from, 
        valid_to, 
        price_min, 
        price_max, 
        language_id, 
        currency_code, 
        sort_by, 
        sort_direction 
      } = {}) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (search) params.append('search', search);
        if (status_id) params.append('status_id', status_id);
        if (prospect_id) params.append('prospect_id', prospect_id);
        if (created_from) params.append('created_from', created_from);
        if (created_to) params.append('created_to', created_to);
        if (valid_from) params.append('valid_from', valid_from);
        if (valid_to) params.append('valid_to', valid_to);
        if (price_min !== undefined && price_min !== null && price_min !== '') params.append('price_min', price_min);
        if (price_max !== undefined && price_max !== null && price_max !== '') params.append('price_max', price_max);
        if (language_id) params.append('language_id', language_id);
        if (currency_code) params.append('currency_code', currency_code);
        if (sort_by) params.append('sort_by', sort_by);
        if (sort_direction) params.append('sort_direction', sort_direction);
        return `/proposals?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.proposals
          ? [
              ...result.proposals.map(({ proposal_id }) => ({ type: 'Proposal', id: proposal_id })),
              { type: 'Proposal', id: 'LIST' },
            ]
          : [{ type: 'Proposal', id: 'LIST' }],
    }),

    // GET /proposals/:id - Fetch proposal by ID
    getProposalById: builder.query({
      query: (id) => `/proposals/${id}`,
      providesTags: (result, error, id) => [{ type: 'Proposal', id }],
    }),

    // POST /proposals - Create new proposal
    createProposal: builder.mutation({
      query: (proposalData) => ({
        url: '/proposals',
        method: 'POST',
        body: proposalData,
      }),
      invalidatesTags: [{ type: 'Proposal', id: 'LIST' }],
    }),

    // PATCH /proposals/:id - Update proposal
    updateProposal: builder.mutation({
      query: ({ id, ...proposalData }) => ({
        url: `/proposals/${id}`,
        method: 'PATCH',
        body: proposalData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Proposal', id },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // DELETE /proposals/:id - Delete proposal
    deleteProposal: builder.mutation({
      query: (id) => ({
        url: `/proposals/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Proposal', id },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // GET /proposals/:id/generate-pdf - Generate PDF for proposal
    generateProposalPdf: builder.query({
      query: ({ id, templateType, options = {} }) => {
        const params = new URLSearchParams();
        params.append('type', templateType);
        
        Object.keys(options).forEach(key => {
          if (options[key] !== null && options[key] !== undefined) {
            if (key === 'customSections' || key === 'prettyProposalOptions') {
              params.append(key, JSON.stringify(options[key]));
            } else {
              params.append(key, options[key]);
            }
          }
        });
        
        return `/proposals/${id}/generate-pdf?${params.toString()}`;
      },
      providesTags: (result, error, { id }) => [{ type: 'PdfSnapshot', id }],
    }),

    // GET /proposals/:id/pdf-snapshots - Get PDF snapshot history
    getPdfSnapshots: builder.query({
      query: (proposalId) => `/proposals/${proposalId}/pdf-snapshots`,
      providesTags: (result, error, proposalId) => [{ type: 'PdfSnapshot', id: proposalId }],
    }),

    // GET /proposals/max-price - Get maximum proposal price for slider range
    getMaxPrice: builder.query({
      query: () => '/proposals/max-price',
      providesTags: ['ProposalMaxPrice'],
    }),

    // GET /proposals/:id/preview - Get proposal preview HTML
    getProposalPreview: builder.query({
      query: ({ proposalId, templateType, options = {} }) => {
        const params = new URLSearchParams();
        params.append('type', templateType || 'product_summary');
        
        Object.keys(options).forEach(key => {
          if (options[key] !== null && options[key] !== undefined) {
            if (typeof options[key] === 'object') {
              params.append(key, JSON.stringify(options[key]));
            } else {
              params.append(key, options[key]);
            }
          }
        });
        
        return `/proposals/${proposalId}/preview?${params.toString()}`;
      },
    }),

    // PUT /proposals/:id/currency - Update proposal currency
    updateProposalCurrency: builder.mutation({
      query: ({ proposalId, currencyCode }) => ({
        url: `/proposals/${proposalId}/currency`,
        method: 'PUT',
        body: { currency_code: currencyCode },
      }),
      invalidatesTags: (result, error, { proposalId }) => [{ type: 'Proposal', id: proposalId }],
    }),

    // PATCH /proposals/:id/pdf-options - Persist PDF generation options for a template
    saveProposalPdfOptions: builder.mutation({
      query: (arg) => {
        const proposalId = typeof arg === 'object' && arg !== null ? arg.proposalId : arg;
        const templateType = typeof arg === 'object' && arg !== null ? arg.templateType : undefined;
        const options = typeof arg === 'object' && arg !== null ? arg.options : undefined;
        return {
          url: `/proposals/${proposalId}/pdf-options`,
          method: 'PATCH',
          body: templateType
            ? { templateType, options: options || {} }
            : {},
        };
      },
      invalidatesTags: [{ type: 'Proposal', id: 'LIST' }],
    }),

    // POST /proposals/:id/send - Mark proposal as sent
    sendProposal: builder.mutation({
      query: (proposalId) => ({
        url: `/proposals/${proposalId}/send`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, proposalId) => [
        { type: 'Proposal', id: proposalId },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // POST /proposals/:id/accept - Accept proposal
    acceptProposal: builder.mutation({
      query: (proposalId) => ({
        url: `/proposals/${proposalId}/accept`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, proposalId) => [
        { type: 'Proposal', id: proposalId },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // POST /proposals/:id/hold - Put proposal on hold
    holdProposal: builder.mutation({
      query: (proposalId) => ({
        url: `/proposals/${proposalId}/hold`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, proposalId) => [
        { type: 'Proposal', id: proposalId },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // POST /proposals/:id/expire - Mark proposal as expired
    expireProposal: builder.mutation({
      query: (proposalId) => ({
        url: `/proposals/${proposalId}/expire`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, proposalId) => [
        { type: 'Proposal', id: proposalId },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // POST /proposals/:id/convert - Convert proposal to order
    convertProposal: builder.mutation({
      query: (proposalId) => ({
        url: `/proposals/${proposalId}/convert`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, proposalId) => [
        { type: 'Proposal', id: proposalId },
        { type: 'Proposal', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // POST /proposals/:id/duplicate - Duplicate proposal for another customer
    duplicateProposal: builder.mutation({
      query: ({ proposalId, ...body }) => ({
        url: `/proposals/${proposalId}/duplicate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { proposalId }) => [
        { type: 'Proposal', id: proposalId },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // POST /proposals/:id/cancel-send - Cancel sent status
    cancelSendProposal: builder.mutation({
      query: (proposalId) => ({
        url: `/proposals/${proposalId}/cancel-send`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, proposalId) => [
        { type: 'Proposal', id: proposalId },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // GET /proposals/:id/status-history - Get status change history
    getProposalStatusHistory: builder.query({
      query: (proposalId) => `/proposals/${proposalId}/status-history`,
      providesTags: (result, error, proposalId) => [{ type: 'Proposal', id: `status_${proposalId}` }],
    }),

    // POST /proposals/temp-upload-item-image - Upload temp item image
    uploadTempImage: builder.mutation({
      query: (formData) => ({
        url: '/proposals/temp-upload-item-image',
        method: 'POST',
        body: formData,
      }),
    }),

    // POST /proposals/upload-custom-section-image - Upload custom section image
    uploadCustomSectionImage: builder.mutation({
      query: (formData) => ({
        url: '/proposals/upload-custom-section-image',
        method: 'POST',
        body: formData,
      }),
    }),

    // POST /proposals/:proposalItemId/upload-item-image - Upload item image
    uploadItemImage: builder.mutation({
      query: ({ proposalItemId, formData }) => ({
        url: `/proposals/${proposalItemId}/upload-item-image`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'ProposalItemImage' }],
    }),

    // POST /proposals/items/:proposalItemId/images - Upload additional item image
    uploadAdditionalItemImage: builder.mutation({
      query: ({ proposalItemId, formData }) => ({
        url: `/proposals/items/${proposalItemId}/images`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'ProposalItemImage' }],
    }),

    // PATCH /proposals/items/images/:imageId/description - Update image description
    updateItemImageDescription: builder.mutation({
      query: ({ imageId, description }) => ({
        url: `/proposals/items/images/${imageId}/description`,
        method: 'PATCH',
        body: { description },
      }),
      invalidatesTags: [{ type: 'ProposalItemImage' }],
    }),

    // DELETE /proposals/items/images/:imageId - Delete item image
    deleteItemImage: builder.mutation({
      query: (imageId) => ({
        url: `/proposals/items/images/${imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ProposalItemImage' }],
    }),

    // POST /proposals/items/:proposalItemId/images/reorder - Reorder item images
    reorderItemImages: builder.mutation({
      query: ({ proposalItemId, imageOrder }) => ({
        url: `/proposals/items/${proposalItemId}/images/reorder`,
        method: 'POST',
        body: { image_order: imageOrder },
      }),
      invalidatesTags: [{ type: 'ProposalItemImage' }],
    }),

    // POST /proposals/:id/items/reorder - Reorder proposal items (touches proposal updated_at)
    reorderProposalItems: builder.mutation({
      query: ({ proposalId, itemOrder }) => ({
        url: `/proposals/${proposalId}/items/reorder`,
        method: 'POST',
        body: { item_order: itemOrder },
      }),
      invalidatesTags: (result, error, { proposalId }) => [
        { type: 'Proposal', id: proposalId },
        { type: 'Proposal', id: 'LIST' },
      ],
    }),

    // POST /proposals/:id/generate-pdf - Generate PDF (mutation for downloading)
    generatePdf: builder.mutation({
      query: ({ id, templateType, options = {} }) => ({
        url: `/proposals/${id}/generate-pdf`,
        method: 'POST',
        body: {
          type: templateType,
          options,
        },
        timeout: 300000,
        responseHandler: async (response) => {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/pdf')) {
            const blob = await response.blob();
            return URL.createObjectURL(blob);
          }
          return response.json();
        },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'PdfSnapshot', id }],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetProposalsQuery,
  useGetProposalByIdQuery,
  useCreateProposalMutation,
  useUpdateProposalMutation,
  useDeleteProposalMutation,
  useGenerateProposalPdfQuery,
  useLazyGenerateProposalPdfQuery,
  useGetPdfSnapshotsQuery,
  useGetProposalPreviewQuery,
  useLazyGetProposalPreviewQuery,
  useUpdateProposalCurrencyMutation,
  useSaveProposalPdfOptionsMutation,
  useSendProposalMutation,
  useAcceptProposalMutation,
  useHoldProposalMutation,
  useExpireProposalMutation,
  useConvertProposalMutation,
  useDuplicateProposalMutation,
  useCancelSendProposalMutation,
  useGetProposalStatusHistoryQuery,
  useUploadTempImageMutation,
  useUploadCustomSectionImageMutation,
  useUploadItemImageMutation,
  useUploadAdditionalItemImageMutation,
  useUpdateItemImageDescriptionMutation,
  useDeleteItemImageMutation,
  useReorderItemImagesMutation,
  useReorderProposalItemsMutation,
  useGeneratePdfMutation,
  useGetMaxPriceQuery,
} = proposalsApi;

